import { useState } from "react";
import { Link } from "react-router-dom";

interface TestimonialItem {
  quote: string;
  name: string;
  project: string;
}

interface TestimonialsProps {
  testimonials?: TestimonialItem[];
}

const defaultTestimonials: TestimonialItem[] = [
  {
    quote: "Westec Builders completely transformed our home. Professional, reliable, and high-quality work from start to finish. The team communicated clearly at every step and delivered exactly what they promised.",
    name: "Sarah M.",
    project: "Kitchen & Bathroom Remodel — Colorado Springs",
  },
  {
    quote: "We hired Westec for a full home addition and they exceeded every expectation. The craftsmanship is outstanding, and they finished right on schedule. Couldn't be happier with the results.",
    name: "James R.",
    project: "Home Addition — Monument, CO",
  },
  {
    quote: "Great communication throughout the entire commercial build-out. Westec Builders understood our business needs and delivered a space that works perfectly for us. Highly recommend.",
    name: "Linda K.",
    project: "Commercial Build-Out — Pueblo, CO",
  },
  {
    quote: "From the initial consultation to the final walkthrough, Westec Builders made the entire process seamless. Our new custom home is everything we dreamed of and built to last.",
    name: "David T.",
    project: "Custom Home Build — Falcon, CO",
  },
  {
    quote: "The attention to detail is unmatched. Every finish, fixture, and structural element was executed perfectly. Our remodel came in on budget and ahead of schedule — remarkable.",
    name: "Patricia W.",
    project: "Full Home Renovation — Castle Rock, CO",
  },
  {
    quote: "Reliable, honest, and highly skilled. Westec Builders set the standard for what a construction company should be. Our project was handled with professionalism from day one.",
    name: "Mike H.",
    project: "Custom Home Build — Colorado Springs",
  },
];

const StarRating = () => (
  <div className="flex gap-0.5 mb-4">
    {[...Array(5)].map((_, i) => (
      <span key={i} className="w-4 h-4 flex items-center justify-center">
        <i className="ri-star-fill text-brand-amber text-sm" />
      </span>
    ))}
  </div>
);

export default function Testimonials({ testimonials }: TestimonialsProps) {
  const items = testimonials ?? defaultTestimonials;
  const [page, setPage] = useState(0);
  const perPage = 3;
  const totalPages = Math.ceil(items.length / perPage);
  const visible = items.slice(page * perPage, page * perPage + perPage);

  return (
    <section className="py-24 bg-brand-warm">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-xs font-body text-brand-amber uppercase tracking-widest font-semibold mb-3">
              Client Testimonials
            </p>
            <h2 className="font-heading font-bold text-4xl lg:text-5xl text-brand-dark leading-tight">
              What Clients<br />
              <span className="text-brand-charcoal/40">Are Saying</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="w-10 h-10 flex items-center justify-center border border-brand-warm-deep rounded-full hover:border-brand-amber disabled:opacity-30 transition-colors cursor-pointer"
              aria-label="Previous testimonials"
            >
              <i className="ri-arrow-left-line text-brand-charcoal" />
            </button>
            <span className="text-xs font-body text-brand-charcoal/60 w-12 text-center">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page === totalPages - 1}
              className="w-10 h-10 flex items-center justify-center bg-brand-dark text-white rounded-full hover:bg-brand-charcoal disabled:opacity-30 transition-colors cursor-pointer"
              aria-label="Next testimonials"
            >
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>

        {/* Testimonial cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {visible.map((item, i) => (
            <article
              key={i}
              className="bg-white rounded-2xl p-7 border border-brand-warm-deep flex flex-col"
            >
              <StarRating />
              <blockquote className="font-body text-sm text-brand-charcoal leading-relaxed flex-1 mb-6 italic">
                &ldquo;{item.quote}&rdquo;
              </blockquote>
              <div className="border-t border-brand-warm-deep pt-5">
                <p className="font-body font-semibold text-brand-dark text-sm">{item.name}</p>
                <p className="font-body text-xs text-brand-amber mt-0.5">{item.project}</p>
              </div>
            </article>
          ))}
        </div>

        {/* Post-testimonials CTA */}
        <div className="bg-brand-dark rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-heading font-bold text-xl text-white mb-1">
              Join 500+ Satisfied Colorado Clients
            </p>
            <p className="font-body text-sm text-white/60">
              Trusted by homeowners and businesses throughout the Pikes Peak region.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <Link
              to="/#estimate"
              className="bg-brand-amber text-white px-6 py-3 rounded-md text-sm font-body font-semibold hover:bg-brand-amber-light transition-colors cursor-pointer whitespace-nowrap text-center"
            >
              Get a Free Estimate
            </Link>
            <a
              href="tel:+17195550100"
              className="border border-white/30 text-white px-6 py-3 rounded-md text-sm font-body font-semibold hover:border-white/60 transition-colors cursor-pointer whitespace-nowrap text-center"
            >
              Call (719) 555-0100
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
